import { useRef, useState } from "react";

import { arc as d3Arc, pie as d3Pie } from "d3";

import { cn } from "@/lib/utils";

import { CarrierLogo } from "@/components/common/CarrierLogo";
import { useUserPreferences } from "@/context/UserPreferencesContext";

import { carrierCodeForLegendEntry } from "./carrierLegend";
import { seriesColor, useChartTheme } from "./theme";
import type {
  ChartInteraction,
  ChartMask,
  ChartVariant,
  PieChartSpec,
} from "./types";
import {
  formatSeriesValue,
  useContainerSize,
  VARIANT_CONFIG,
} from "./useChartModel";

const DEFAULT_MIN_SLICES = 6;
const OTHER_LABEL = "Other";
const MIN_DONUT = 64;
const LEGEND_MIN_W = 116;
const LEGEND_MAX_W = 184;
const GAP = 12;
const SVG_PAD = 6;

interface Slice {
  label: string;
  value: number;
  key: string;
}

/** Keep the top-N slices by value; roll the rest into a single "Other". */
function collapseSlices(data: PieChartSpec["data"], minSlices: number): Slice[] {
  const keep = Math.max(0, Math.floor(minSlices));
  const clean: Slice[] = data.map((d) => ({
    label: d.title ?? "—",
    value: Math.max(0, d.value),
    key: d.key ?? d.title ?? "—",
  }));
  if (keep === 0 || clean.length <= keep) return clean;

  const sorted = [...clean].sort((a, b) => b.value - a.value);
  const kept = sorted.slice(0, keep);
  const otherValue = sorted.slice(keep).reduce((sum, d) => sum + d.value, 0);
  return [...kept, { label: OTHER_LABEL, value: otherValue, key: OTHER_LABEL }];
}

type PieLayout = "row" | "column";

interface PieGeometry {
  layout: PieLayout;
  donut: number;
  legendMaxWidth: number;
  labelSize: number;
  valueSize: number;
  pctSize: number;
  showCenterLabel: boolean;
  showCenterPct: boolean;
}

/**
 * Fit donut + legend into the measured tile. Side-by-side when that yields a
 * larger ring; stacked when the tile is tall and narrow.
 */
function resolvePieGeometry(
  width: number,
  height: number,
  sliceCount: number,
  fallbackHeight: number,
  baseFont: number,
): PieGeometry {
  const w = Math.max(0, width);
  const h = height > 0 ? height : fallbackHeight;
  const legendH = Math.max(1, sliceCount) * 18;
  const legendBudget = clamp(LEGEND_MIN_W, Math.round(w * 0.4), LEGEND_MAX_W);

  const rowDonut = Math.min(h, Math.max(0, w - GAP - legendBudget));
  const colDonut = Math.min(w, Math.max(0, h - GAP - Math.min(legendH, h * 0.45)));
  const layout: PieLayout =
    rowDonut >= MIN_DONUT && rowDonut + 12 >= colDonut ? "row" : "column";

  const raw = layout === "row" ? rowDonut : colDonut;
  const donut = Math.max(MIN_DONUT, Math.floor(raw));
  const labelSize = clamp(8, Math.round(donut / 18), baseFont);
  const valueSize = clamp(10, Math.round(donut / 12), baseFont + 4);
  const pctSize = Math.max(8, labelSize - 1);

  return {
    layout,
    donut,
    legendMaxWidth: layout === "row" ? legendBudget : LEGEND_MAX_W + 72,
    labelSize,
    valueSize,
    pctSize,
    showCenterLabel: donut >= 88,
    showCenterPct: donut >= 110,
  };
}

function clamp(min: number, value: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function truncateLabel(label: string, donut: number, fontSize: number): string {
  const hole = donut * 0.58 - SVG_PAD * 2;
  const maxChars = Math.max(6, Math.floor(hole / (fontSize * 0.58)));
  if (label.length <= maxChars) return label;
  return `${label.slice(0, maxChars - 1)}…`;
}

/** Donut chart with hover highlight, center readout, and a value legend. */
export function PieChart({
  spec,
  variant,
  mask,
  onCategoryClick,
  selectedCategory = null,
  replayNonce = 0,
}: {
  spec: PieChartSpec;
  variant: ChartVariant;
  mask?: ChartMask;
} & ChartInteraction) {
  const ref = useRef<HTMLDivElement>(null);
  const theme = useChartTheme();
  const { preferences } = useUserPreferences();
  const [active, setActive] = useState<number | null>(null);

  const cfg = VARIANT_CONFIG[variant];
  const container = useContainerSize(ref, { width: 320, height: cfg.height });
  const slices = collapseSlices(spec.data, spec.minSlices ?? DEFAULT_MIN_SLICES);
  const geometry = resolvePieGeometry(
    container.width,
    container.height,
    slices.length,
    cfg.height,
    cfg.fontSize,
  );

  const radius = Math.max(12, geometry.donut / 2 - SVG_PAD);
  const hoverGrow = Math.max(2, Math.round(radius * 0.045));
  const total = slices.reduce((sum, s) => sum + s.value, 0);

  const arcs = d3Pie<Slice>()
    .value((d) => d.value)
    .sort(null)(slices);

  const arcGen = d3Arc<(typeof arcs)[number]>()
    .innerRadius(radius * 0.58)
    .outerRadius(radius);
  const activeArcGen = d3Arc<(typeof arcs)[number]>()
    .innerRadius(radius * 0.58)
    .outerRadius(radius + hoverGrow);

  const selectedIndex = (() => {
    if (selectedCategory == null) return null;
    const idx = slices.findIndex(
      (s) => s.label === selectedCategory || s.key === selectedCategory,
    );
    return idx >= 0 ? idx : null;
  })();
  const emphasis = active ?? selectedIndex;

  const handleSliceClick = (slice: Slice) => {
    onCategoryClick?.({ category: slice.label, categoryKey: slice.key });
  };

  const maskCategory = Boolean(mask?.category || mask?.series);
  const centerLabel = emphasis != null ? slices[emphasis].label : "Total";
  const fmt = (v: number) => formatSeriesValue(v, spec.valueFormat);
  const centerValue =
    emphasis != null ? fmt(slices[emphasis].value) : fmt(total);
  const centerPct =
    emphasis != null && total > 0
      ? `${((slices[emphasis].value / total) * 100).toFixed(1)}%`
      : null;
  const showPct = geometry.showCenterPct && centerPct != null;

  return (
    <div
      ref={ref}
      className={cn(
        "flex h-full min-h-0 w-full overflow-hidden",
        geometry.layout === "row"
          ? "flex-row items-center justify-start gap-3"
          : "flex-col items-center justify-center gap-2",
        replayNonce > 0 && "chart-static",
      )}
    >
      <svg
        width={geometry.donut}
        height={geometry.donut}
        role="img"
        aria-label={spec.title ?? "Pie chart"}
        className="block shrink-0"
      >
        <g transform={`translate(${geometry.donut / 2},${geometry.donut / 2})`}>
          <g key={replayNonce} className="chart-pie">
            {arcs.map((slice, i) => {
              const isActive = active === i || selectedIndex === i;
              const dimmed =
                (active != null || selectedIndex != null) && !isActive;
              return (
                <path
                  key={slice.data.label}
                  d={(isActive ? activeArcGen : arcGen)(slice) ?? undefined}
                  fill={seriesColor(theme.series, i)}
                  opacity={dimmed ? 0.4 : 1}
                  stroke="var(--color-card)"
                  strokeWidth={1}
                  style={onCategoryClick ? { cursor: "pointer" } : undefined}
                  onMouseEnter={() => setActive(i)}
                  onMouseLeave={() => setActive((cur) => (cur === i ? null : cur))}
                  onClick={
                    onCategoryClick
                      ? () => handleSliceClick(slice.data)
                      : undefined
                  }
                />
              );
            })}
          </g>
          {geometry.showCenterLabel && (
            <text
              textAnchor="middle"
              dominantBaseline="middle"
              y={showPct ? -12 : -6}
              fontSize={geometry.labelSize}
              fill={theme.axis}
              className={
                maskCategory && emphasis != null ? "sensitive-blur" : undefined
              }
            >
              {truncateLabel(centerLabel, geometry.donut, geometry.labelSize)}
            </text>
          )}
          <text
            textAnchor="middle"
            dominantBaseline="middle"
            y={geometry.showCenterLabel ? (showPct ? 6 : 10) : 0}
            fontSize={geometry.valueSize}
            fontWeight={600}
            fill="var(--color-foreground)"
            className={
              mask?.value || maskCategory ? "sensitive-blur" : undefined
            }
          >
            {centerValue}
          </text>
          {showPct && (
            <text
              textAnchor="middle"
              dominantBaseline="middle"
              y={geometry.showCenterLabel ? 22 : 14}
              fontSize={geometry.pctSize}
              fill={theme.axis}
              className={maskCategory ? "sensitive-blur" : undefined}
            >
              {centerPct}
            </text>
          )}
        </g>
      </svg>

      <ul
        className={cn(
          "flex min-h-0 min-w-0 flex-col gap-1 overflow-y-auto",
          geometry.layout === "row" ? "w-full shrink-0" : "w-full",
        )}
        style={{ maxWidth: geometry.legendMaxWidth }}
      >
        {slices.map((slice, i) => {
          const selected = selectedIndex === i;
          const carrierCode = carrierCodeForLegendEntry(
            spec.carrierLegend,
            "category",
            slice.key,
            slice.label,
          );
          const row = (
            <>
              <span className="flex min-w-0 items-center gap-1.5 text-muted-foreground">
                <span
                  className="inline-block size-2.5 shrink-0 rounded-[3px]"
                  style={{ backgroundColor: seriesColor(theme.series, i) }}
                />
                {carrierCode ? (
                  <CarrierLogo
                    carrier={carrierCode}
                    size={14}
                    showLogo={preferences.showCarrierLogos}
                    logoOnly={preferences.showCarrierLogos}
                    className={cn(
                      preferences.showCarrierLogos && "max-h-[14px]",
                      maskCategory && "sensitive-blur",
                    )}
                  />
                ) : (
                  <span className={cn("truncate", maskCategory && "sensitive-blur")}>
                    {slice.label}
                  </span>
                )}
              </span>
              <span
                className={cn(
                  "shrink-0 text-right font-medium tabular-nums text-foreground",
                  (mask?.value || maskCategory) && "sensitive-blur",
                )}
              >
                {fmt(slice.value)}
              </span>
            </>
          );
          const rowClass = cn(
            "flex items-center justify-between gap-3 text-[11px]",
            onCategoryClick && "cursor-pointer rounded-sm hover:text-foreground",
            selected && "font-medium text-foreground",
            selectedIndex != null && !selected && "opacity-50",
          );
          if (onCategoryClick) {
            return (
              <li key={slice.label}>
                <button
                  type="button"
                  aria-pressed={selected}
                  className={cn(rowClass, "w-full border-0 bg-transparent p-0 text-left")}
                  onMouseEnter={() => setActive(i)}
                  onMouseLeave={() => setActive((cur) => (cur === i ? null : cur))}
                  onClick={() => handleSliceClick(slice)}
                >
                  {row}
                </button>
              </li>
            );
          }
          return (
            <li
              key={slice.label}
              className={rowClass}
              onMouseEnter={() => setActive(i)}
              onMouseLeave={() => setActive((cur) => (cur === i ? null : cur))}
            >
              {row}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
